import { __assign } from "tslib";
import * as ltjsEnvironment from '../../ltjsEnvironment';
import PickResult from '../../pickResults/PickResult';
import CrosstabPickResultEntry from '../../crosstab/CrosstabPickResultEntry';
import DataGridPickResultEntry from '../../datagrid/DataGridPickResultEntry';
import Display from '../popover/Display';
import * as StaticContainerUtil from '../../util/StaticContainerUtil';
import Tooltip from './Tooltip';
import Position from '../popover/Position';
function isLabelValuePickResultDetails(details) {
    var LabelValuePickResultDetails = ltjsEnvironment.moduleLookup('gtl::LabelValuePickResultDetails');
    return details instanceof LabelValuePickResultDetails;
}
// Work around the fact that we don't have a graph specific visualization manager.
var _gtlLabelProcessor = null;
ltjsEnvironment.runWhenInitialized(function () {
    // sas/ltjs/gtl/lib.js has a dependency on sas/ltjs/gtl/models/LabelValuePickResultDetails
    //   If the module lookup is null, that means gtl is not loaded.
    var LabelValuePickResultDetails = ltjsEnvironment.typeMap.getModuleByName('gtl::LabelValuePickResultDetails');
    if (LabelValuePickResultDetails) {
        _gtlLabelProcessor = function (pickResult) {
            var details = pickResult.getDetails();
            if (details && isLabelValuePickResultDetails(details) && details.getIsTruncated()) {
                return [
                    {
                        label: details.getLabel(),
                        value: details.getFormattedValue(),
                    },
                ];
            }
            return null;
        };
    }
});
var currentZIndex = 30;
// This is used to make an overestimation on the max number of data items that can be displayed in a tooltip.
var NAIVE_HEIGHT = 12;
// These constants are only used for time based callout tips.
var CALLOUT_OFFSET = 50;
var CALLOUT_STRAIGHT_SEGMENT_LENGTH = 15;
var TIP_PADDING = 5;
var _getNextZIndex = function () {
    return currentZIndex;
};
if (sas.ltjs.isOpenUI) {
    sap.ui.require('sap.ui.core.Popup');
    _getNextZIndex = function () {
        var Popup = sap.ui.core.Popup;
        if (currentZIndex !== Popup.getLastZIndex()) {
            currentZIndex = Popup.getNextZIndex();
        }
        return currentZIndex;
    };
}
/**
 * This called on timeout to display the current tooltips.
 * @param {Array.<Tooltip>} tooltipPool The current pool of tooltips.
 * @param {Array.<Object>} currentTipData The current tooltip data to be rendered.
 * @param {boolean} [reusePosition=false] Reuse the position from the previous positioning.
 * @param {Object} [mouseLocation=null] The current mouse position, will be used as the tooltip location if present.
 * @param {string} [containerId=null] Required if mouseLocation is set. Used for converting mouseLocation to global coordinates.
 * @private
 */
function _displayTooltips(tooltipPool, currentTipData, reusePosition, mouseLocation, containerId) {
    var numTips = tooltipPool.length - 1;
    var staticSpace = StaticContainerUtil.getStaticSpace();
    for (var i = 0, len = currentTipData.length; i < len; ++i) {
        if (i > numTips) {
            var newTip = new Tooltip();
            newTip.setDisplay(Display.NONE, false);
            tooltipPool.push(newTip);
        }
        var data = currentTipData[i];
        var tip = tooltipPool[i];
        tip.setStyleOverrides(data.overrideStyles);
        tip.setTooltipValues(data.values, data.naiveLength);
        tip.renderPopover(staticSpace, _getNextZIndex(currentZIndex));
        if (mouseLocation) {
            var container = document.getElementById(containerId);
            var bounds = void 0;
            if (container) {
                bounds = container.getBoundingClientRect();
                tip.positionPopover({
                    left: bounds.left + mouseLocation.x,
                    top: bounds.top + mouseLocation.y,
                    width: 0,
                    height: 0,
                }, null, reusePosition);
            }
        }
        else {
            tip.positionPopover(data.preferredGlobalCoordinates, null, reusePosition);
        }
        tip.setDisplay(Display.VISIBLE);
    }
}
/**
 * A stricter object comparison
 * @param {boolean} [includeLocation=true] include location in comparison
 * @returns {boolean} true is tip1 is equal than tip2.
 * @private
 */
function _tipsEqual(tip1, tip2, includeLocation) {
    if (tip1.length !== tip2.length) {
        return false;
    }
    if (includeLocation !== false) {
        // multi tooltips do not have locations.
        if (tip1.location[0] !== tip2.location[0] || tip1.location[1] !== tip2.location[1]) {
            return false;
        }
    }
    for (var i = 0, len = tip1.length; i < len; ++i) {
        var value1 = tip1[i];
        var value2 = tip2[i];
        if (value1.label !== value2.label || value1.value !== value2.value) {
            return false;
        }
    }
    return true;
}
/**
 * helper function to tell if the tooltip data has changed.
 * @param {Object} data1
 * @param {Object} data2
 * @returns {boolean} true is data1 is different than data2. false otherwise.
 * @private
 */
function _tipDataChanged(data1, data2) {
    if (!data1 && !data2) {
        return false;
    }
    if (!data1 || !data2) {
        return true;
    }
    if (data1.length !== data2.length) {
        return true;
    }
    for (var i = 0, len = data1.length; i < len; ++i) {
        var tipdata1 = data1[i];
        var tipdata2 = data2[i];
        if (tipdata1.containerId !== tipdata2.containerId) {
            return true;
        }
        if (tipdata1.values.length !== tipdata2.values.length) {
            return true;
        }
        for (var j = 0, len2 = tipdata1.values.length; j < len2; ++j) {
            // Don't check coordinates because of Crosstab and table. Maybe that's ok, but it looks like a bug.
            if (!_tipsEqual(tipdata1.values[j], tipdata2.values[j], false)) {
                return true;
            }
        }
    }
    return false;
}
/**
 * Checks if data1 contains at least some of the values from data2.
 * @private
 */
function _tipContains(data1, data2) {
    for (var i = 0, len = data1.length; i < len; ++i) {
        var tip1 = data1[i];
        for (var j = 0, len2 = data2.length; j < len2; ++j) {
            var tip2 = data2[j];
            if (_tipsEqual(tip1, tip2)) {
                return true;
            }
        }
    }
    return false;
}
/**
 * Default pick result processor.
 * @param {PickResult} pickResult the PickResult to check.
 * @returns {Array.<Object.<string,string>} the tooltip values for the current pick result.
 * @private
 */
function _defaultPickResultProcessor(pickResult) {
    var entries = pickResult.getEntries();
    if (!entries || entries.length <= 0) {
        return null;
    }
    var tooltipItems = [];
    // NOTE(mikcol): legacy created numEntries here and checked it for > 0
    var addedEntries = {};
    for (var _i = 0, entries_1 = entries; _i < entries_1.length; _i++) {
        var entry = entries_1[_i];
        if (!entry || !entry.getUseAsTooltip()) {
            continue;
        }
        var label = entry.getLabel();
        var value = entry.getFormattedValue();
        var currentEntry = void 0;
        if (entry instanceof CrosstabPickResultEntry || entry instanceof DataGridPickResultEntry) {
            if (value) {
                currentEntry = label ? label + ':' + value : value;
            }
            else if (label) {
                currentEntry = label;
            }
        }
        else if ((label || label === '') && (value || value === '')) {
            currentEntry = label + ':' + value;
        }
        if (currentEntry) {
            // Skip this entry if we already have it
            if (!addedEntries[currentEntry]) {
                addedEntries[currentEntry] = true;
                tooltipItems.push({ label: label, value: value, swatchSVG: entry.getLegendSVG() });
            }
        }
    }
    if (tooltipItems.length === 0) {
        return null;
    }
    return tooltipItems;
}
function _createTooltipItemsFromCustomData(customData, containerId) {
    var resValues = [];
    var resCoordinates = undefined;
    if (!Array.isArray(customData)) {
        customData = [customData];
    }
    var container = document.getElementById(containerId);
    var bounds;
    if (container) {
        bounds = container.getBoundingClientRect();
    }
    // NOTE(mikcol): count was undefined, so count++ => NaN and results.naiveLength would probably usually be NaN
    var count = 0, naiveHeight = 0;
    for (var i = 0, len = customData.length; i < len; ++i) {
        // NOTE(mikcol): legacy code bounds assumes bounds is not undefined/null
        bounds = bounds;
        var data = customData[i];
        // NOTE(mikcol): legacy assumes this data is populated
        var targetLocation = data.getTarget();
        var values = data.getValues().concat();
        // NOTE(mikcol): used to be values.location = targetLocation
        values.location = [targetLocation.x, targetLocation.y];
        values.targetLocation = [targetLocation.x, targetLocation.y];
        resValues.push(values);
        if (naiveHeight > bounds.height) {
            count++;
            naiveHeight += values.length * NAIVE_HEIGHT;
        }
        if (!resCoordinates) {
            var yCoordinate = targetLocation.y;
            if (data._targetFlipped) {
                yCoordinate = bounds.height - targetLocation.y;
            }
            resCoordinates = {
                left: targetLocation.x + bounds.left,
                top: yCoordinate + bounds.top,
                width: targetLocation.width,
                height: targetLocation.height,
            };
        }
    }
    return {
        containerId: containerId,
        preferredGlobalCoordinates: resCoordinates,
        values: resValues,
        naiveLength: count,
    };
}
/**
 * Process pick result to see if it is a tooltip pick result.
 *  It will iterate through all of the pick result processors and return the first result.
 *  This is needed to avoid GTL references in TooltipManager
 * @param {PickResult} pickResult the PickResult to process.
 * @param {string} the id of the container associated with the pick results.
 * @returns {Array.<Object.<string,string>} the tooltip values for the current pick result.
 * @private
 */
function _createTooltipItemsFromPickResult(pickResults, containerId) {
    var resValues = [];
    var resCoordinates = undefined;
    var count = 0;
    var naiveHeight = 0;
    if (pickResults && pickResults.length) {
        var container = document.getElementById(containerId);
        var bounds = void 0;
        if (container) {
            bounds = container.getBoundingClientRect();
        }
        for (var _i = 0, pickResults_1 = pickResults; _i < pickResults_1.length; _i++) {
            var pickResult = pickResults_1[_i];
            if (!pickResult) {
                continue;
            }
            var processedResult = null;
            if (_gtlLabelProcessor) {
                processedResult = _gtlLabelProcessor(pickResult);
            }
            if (!processedResult) {
                processedResult = _defaultPickResultProcessor(pickResult);
            }
            if (processedResult) {
                bounds = bounds;
                resValues.push(processedResult);
                if (naiveHeight < bounds.height) {
                    count++;
                    naiveHeight += processedResult.length * NAIVE_HEIGHT;
                }
                // results.coordinates is only used with single item tooltips, so no
                //     additional logic for multiple item is needed.
                var loc = pickResult.getPreferredTargetLocation();
                processedResult.location = [loc[0] + bounds.left, bounds.height - loc[1] + bounds.top];
                processedResult.targetLocation = [processedResult.location[0], processedResult.location[1]];
                if (resCoordinates === undefined) {
                    resCoordinates = {
                        left: processedResult.location[0],
                        top: processedResult.location[1],
                        width: 0,
                        height: 0,
                    };
                }
            }
        }
    }
    return {
        containerId: containerId,
        preferredGlobalCoordinates: resCoordinates,
        values: resValues,
        naiveLength: count,
    };
}
function _allTooltipsAreVisible(tooltipPool) {
    for (var i = 0, len = tooltipPool.length; i < len; ++i) {
        if (tooltipPool[i].getDisplay() !== Display.VISIBLE) {
            return false;
        }
    }
    return true;
}
function _sortMatchingYLocationsByX(tipData, tipIndices, smallerXGoesHigher) {
    // to help avoid collision of callout lines sort things that are at the
    // same y by their x value
    for (var i = 1; i < tipIndices.length; ++i) {
        var location1 = tipData[tipIndices[i]].values[0].location;
        var location2 = tipData[tipIndices[i - 1]].values[0].location;
        var newIndex = i;
        while (location1[1] === location2[1]) {
            if ((smallerXGoesHigher && (newIndex === 1 || location2[0] > location1[0])) ||
                (!smallerXGoesHigher && (newIndex === 1 || location2[0] < location1[0]))) {
                break;
            }
            --newIndex;
            location2 = tipData[tipIndices[newIndex - 1]].values[0].location;
        }
        if (newIndex !== i) {
            var temp = tipIndices[i];
            tipIndices[i] = tipIndices[newIndex];
            tipIndices[newIndex] = temp;
        }
    }
}
function _refreshSVGContainer() {
    var staticSpace = StaticContainerUtil.getStaticSpace();
    var svgContainer = staticSpace.querySelector('.sasGraphTooltipCalloutLinesAndMarkers');
    if (!svgContainer) {
        svgContainer = document.createElement('div');
        svgContainer.style.overflow = 'visible';
        svgContainer.style.zIndex = String(_getNextZIndex(currentZIndex));
        svgContainer.style.pointerEvents = 'none';
        svgContainer.style.position = 'fixed';
        svgContainer.classList.add('sasGraphTooltipCalloutLinesAndMarkers');
        staticSpace.appendChild(svgContainer);
        var svgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svgElement.style.pointerEvents = 'none';
        svgElement.style.position = 'fixed';
        svgElement.style.left = '0px';
        svgElement.style.top = '0px';
        svgElement.style.width = '100%';
        svgElement.style.height = '100%';
        svgContainer.appendChild(svgElement);
    }
    else {
        var svgElement = svgContainer.firstChild;
        if (svgElement) {
            while (svgElement.firstChild) {
                svgElement.removeChild(svgElement.firstChild);
            }
        }
    }
    return svgContainer;
}
function _drawTimeCalloutLinesAndMarkers(container, location, targetLocation, positionLeft, medianX) {
    var offset = positionLeft ? -CALLOUT_OFFSET : CALLOUT_OFFSET;
    var calloutStraightSegmentLength = positionLeft
        ? CALLOUT_STRAIGHT_SEGMENT_LENGTH
        : -CALLOUT_STRAIGHT_SEGMENT_LENGTH;
    var markerRadius = 2.5;
    var svgElement = container.children[0];
    var x1num = targetLocation[0] + offset - (targetLocation[0] - medianX);
    var x1 = x1num.toString();
    var x2 = (x1num + calloutStraightSegmentLength).toString();
    var x3 = targetLocation[0].toString();
    var y1 = location[1].toString();
    var y2 = targetLocation[1].toString();
    var pathData = 'M ' + x1 + ',' + y1 + ' L ' + x2 + ',' + y1 + ' L ' + x3 + ',' + y2;
    var markerBackgroundElement = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    markerBackgroundElement.setAttribute('class', 'sasTimePickMarkerBackground');
    markerBackgroundElement.setAttribute('cx', targetLocation[0].toString());
    markerBackgroundElement.setAttribute('cy', targetLocation[1].toString());
    markerBackgroundElement.setAttribute('r', markerRadius + 1 + 'px');
    svgElement.append(markerBackgroundElement);
    var backPathElement = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    backPathElement.setAttribute('class', 'sasTipCalloutBackground');
    backPathElement.setAttribute('d', pathData);
    backPathElement.setAttribute('fill', 'transparent');
    svgElement.append(backPathElement);
    var markerElement = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    markerElement.setAttribute('class', 'sasTimePickMarker');
    markerElement.setAttribute('cx', targetLocation[0].toString());
    markerElement.setAttribute('cy', targetLocation[1].toString());
    markerElement.setAttribute('r', markerRadius + 'px');
    svgElement.append(markerElement);
    var pathElement = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    pathElement.setAttribute('class', 'sasTipCallout');
    pathElement.setAttribute('d', pathData);
    pathElement.setAttribute('fill', 'transparent');
    svgElement.append(pathElement);
}
function _findMedianX(tipData) {
    if (tipData.length === 2) {
        return tipData[1].values[0].location[0];
    }
    var xLocations = [];
    // index 0 is the axis item
    for (var i = 1; i < tipData.length; i++) {
        var tooltipData = tipData[i];
        xLocations[i - 1] = tooltipData.values[0].location[0];
    }
    xLocations.sort(function (a, b) {
        return a - b;
    });
    // If we have an odd number of indices, the middle index points to
    // the median. If it's even then we average the two middle values
    var middleIndex = Math.floor(xLocations.length / 2);
    return xLocations.length % 2 !== 0
        ? xLocations[middleIndex]
        : (xLocations[middleIndex - 1] + xLocations[middleIndex]) / 2;
}
function _resolveTimeTipCollision(tipData, tipPool, higherTipIndex, lowerTipIndex, moveLowerTip, lowerIsAxis) {
    var higherTipData = tipData[higherTipIndex];
    var higherTip = tipPool[higherTipIndex];
    var higherTipExtent = higherTipData.values[0].location[1] + higherTip.getDomRef().clientHeight / 2 + TIP_PADDING;
    var lowerTipData = tipData[lowerTipIndex];
    var lowerTip = tipPool[lowerTipIndex];
    var overlap = higherTipExtent - lowerTipData.values[0].location[1];
    if (!lowerIsAxis)
        overlap += lowerTip.getDomRef().clientHeight / 2;
    if (overlap > 0) {
        if (moveLowerTip) {
            lowerTipData.values[0].location[1] += overlap;
        }
        else {
            higherTipData.values[0].location[1] -= overlap;
        }
        return true;
    }
    return false;
}
function _layoutTimeTips(tipData, tipPool, indices) {
    if (indices.length >= 2) {
        // determine a vertical offset from the target
        // so that tips do not overlap. start in the middle
        // and push everything out.
        var center = Math.floor(indices.length / 2);
        for (var distanceFromCenter = 0; distanceFromCenter < tipData.length; distanceFromCenter += 1) {
            var higherTipIndex = center - distanceFromCenter;
            if (higherTipIndex > 0) {
                _resolveTimeTipCollision(tipData, tipPool, indices[higherTipIndex - 1], indices[higherTipIndex], false, false);
            }
            var lowerTipIndex = center + distanceFromCenter;
            if (lowerTipIndex < indices.length - 1) {
                _resolveTimeTipCollision(tipData, tipPool, indices[lowerTipIndex], indices[lowerTipIndex + 1], true, false);
            }
        }
    }
    if (indices.length > 0) {
        // if there's a collision with the axis tip move things up as needed
        var axisCollision = _resolveTimeTipCollision(tipData, tipPool, indices[indices.length - 1], 0, false, true);
        if (axisCollision) {
            for (var i = indices.length - 2; i >= 0; --i) {
                _resolveTimeTipCollision(tipData, tipPool, indices[i], indices[i + 1], false, false);
            }
        }
    }
}
var _instanceCheck = {};
var _instance = null;
function isPickResultArray(pickdata) {
    if (Array.isArray(pickdata)) {
        for (var _i = 0, pickdata_1 = pickdata; _i < pickdata_1.length; _i++) {
            var item = pickdata_1[_i];
            if (item instanceof PickResult) {
                return true;
            }
        }
    }
    return false;
}
var TooltipManager = /** @class */ (function () {
    function TooltipManager(check) {
        this._tooltipPool = [];
        this._currentTipData = [];
        this._timeoutId = null;
        if (check !== _instanceCheck) {
            throw new Error('Call TooltipManager.instance(),  not new TooltipManager()!!');
        }
    }
    TooltipManager.prototype.setTooltips = function (pickdata, containerId, overrideStyles, mouseLocation) {
        var _this = this;
        var newTipData = [];
        if (pickdata) {
            var results = void 0;
            // NOTE(mikcol) removed length truthy check
            if (isPickResultArray(pickdata)) {
                results = _createTooltipItemsFromPickResult(pickdata, containerId);
            }
            else {
                results = _createTooltipItemsFromCustomData(pickdata, containerId);
            }
            if (results && results.values.length) {
                // NOTE(mikcol): legacy created a new object, changed to mutate the obj
                // NOTE(mikcol): changed the create methods to return new objects
                // NOTE(mikcol): adds containerId string, renamed coordinates => globalPreferredCoordinates
                results.overrideStyles = overrideStyles;
                newTipData.push(results);
            }
        }
        var dataChanged = _tipDataChanged(newTipData, this._currentTipData);
        if (newTipData.length === 1 &&
            this._currentTipData.length === 1 &&
            _tipContains(newTipData[0].values, this._currentTipData[0].values)) {
            if (this._timeoutId) {
                clearTimeout(this._timeoutId);
                this._timeoutId = window.setTimeout(
                // NOTE(mikcol): legacy took pool, data args
                function () {
                    if (_this._currentTipData[0].values.length === 1) {
                        mouseLocation = null;
                    }
                    _displayTooltips(_this._tooltipPool, _this._currentTipData, false, mouseLocation, containerId);
                    _this._timeoutId = null;
                }, 200);
            }
            else {
                if (newTipData[0].values.length === 1) {
                    mouseLocation = null;
                }
                _displayTooltips(this._tooltipPool, newTipData, true, mouseLocation, containerId);
            }
        }
        else if (dataChanged || !_allTooltipsAreVisible(this._tooltipPool)) {
            // if the data is different or the tips are hidden, then schedule display
            if (this._timeoutId !== null) {
                clearTimeout(this._timeoutId);
                this._timeoutId = null;
            }
            this.hideTooltips();
            if (newTipData.length) {
                this._timeoutId = window.setTimeout(
                // NOTE(mikcol): legacy took pool, data args
                function () {
                    _displayTooltips(_this._tooltipPool, _this._currentTipData);
                    _this._timeoutId = null;
                }, 200);
            }
        }
        if (dataChanged) {
            this._currentTipData = newTipData;
        }
    };
    TooltipManager.prototype.setTimeTooltips = function (pickdata, containerId, overrideStyles) {
        var _this = this;
        var newTipData = [];
        if (pickdata) {
            var results = _createTooltipItemsFromPickResult(pickdata, containerId);
            if (results && results.values.length) {
                results.overrideStyles = overrideStyles;
                for (var _i = 0, _a = results.values; _i < _a.length; _i++) {
                    var value = _a[_i];
                    newTipData.push(__assign(__assign({}, results), { values: [value] }));
                }
            }
        }
        var dataChanged = _tipDataChanged(newTipData, this._currentTipData);
        if (dataChanged) {
            // if the data is different or the tips are hidden, then schedule display
            if (this._timeoutId !== null) {
                clearTimeout(this._timeoutId);
                this._timeoutId = null;
            }
            this.hideTooltips();
            this._timeoutId = window.setTimeout(
            // NOTE(mikcol): legacy took pool, data args
            function () {
                if (!_this._currentTipData) {
                    return;
                }
                // sort by y location
                var axisItem = _this._currentTipData[0];
                _this._currentTipData.sort(function (a, b) {
                    // make sure the axis tip stays at index 0
                    if (a === axisItem) {
                        return -1;
                    }
                    if (b === axisItem) {
                        return 1;
                    }
                    var location1 = a.values[0].location;
                    var location2 = b.values[0].location;
                    return location1[1] - location2[1];
                });
                var medianX = _findMedianX(_this._currentTipData);
                var leftTipIndices = [];
                var rightTipIndices = [];
                var staticSpace = StaticContainerUtil.getStaticSpace();
                var numTips = _this._tooltipPool.length - 1;
                // things that fall exactly at the median get split evenly
                // between the left and right list
                var nextMedianMatchGoesLeft = true;
                for (var i = 0, len = _this._currentTipData.length; i < len; ++i) {
                    if (i > numTips) {
                        var newTip = new Tooltip();
                        if (i > 0) {
                            newTip.setShowArrow(false);
                        }
                        _this._tooltipPool.push(newTip);
                    }
                    var tip = _this._tooltipPool[i];
                    var data = _this._currentTipData[i];
                    tip.setStyleOverrides(data.overrideStyles);
                    tip.setTooltipValues(data.values, 1);
                    // needs to be visible and rendered to be measured
                    tip.setDisplay(Display.VISIBLE);
                    tip.renderPopover(staticSpace, _getNextZIndex(currentZIndex));
                    if (i > 0) {
                        var location_1 = _this._currentTipData[i].values[0].location;
                        var tip_1 = _this._tooltipPool[i];
                        var domRef = tip_1.getDomRef();
                        if (domRef) {
                            var wouldFallOffRight = location_1[0] + domRef.clientWidth + CALLOUT_OFFSET > window.innerWidth;
                            var wouldFallOffLeft = location_1[0] - domRef.clientWidth - CALLOUT_OFFSET < 0;
                            if (wouldFallOffLeft) {
                                rightTipIndices.push(i);
                            }
                            else if (wouldFallOffRight) {
                                leftTipIndices.push(i);
                            }
                            else if (location_1[0] < medianX) {
                                leftTipIndices.push(i);
                            }
                            else if (location_1[0] > medianX) {
                                rightTipIndices.push(i);
                            }
                            else if (nextMedianMatchGoesLeft) {
                                leftTipIndices.push(i);
                                nextMedianMatchGoesLeft = false;
                            }
                            else {
                                rightTipIndices.push(i);
                                nextMedianMatchGoesLeft = true;
                            }
                        }
                    }
                }
                _sortMatchingYLocationsByX(_this._currentTipData, leftTipIndices, true);
                _sortMatchingYLocationsByX(_this._currentTipData, rightTipIndices, false);
                _layoutTimeTips(_this._currentTipData, _this._tooltipPool, leftTipIndices);
                _layoutTimeTips(_this._currentTipData, _this._tooltipPool, rightTipIndices);
                var svgContainer = _refreshSVGContainer();
                for (var i = 0; i < _this._currentTipData.length; ++i) {
                    var data = _this._currentTipData[i];
                    var tip = _this._tooltipPool[i];
                    var location_2 = data.values[0].location;
                    var targetLocation = data.values[0].targetLocation;
                    var goLeft = leftTipIndices.indexOf(i) !== -1;
                    var offset = goLeft ? -CALLOUT_OFFSET : CALLOUT_OFFSET;
                    var domRef = tip.getDomRef();
                    if (domRef) {
                        if (location_2[1] <= domRef.clientHeight / 2) {
                            tip.setDisplay(Display.NONE);
                        }
                        else {
                            if (i === 0) {
                                // this is the tip that displays over the axis
                                tip.setPositions([Position.BOTTOM]);
                                tip.positionPopover({ left: location_2[0], top: location_2[1], width: 0, height: 0 }, null, false);
                            }
                            else {
                                tip.setPositions(goLeft ? [Position.LEFT] : [Position.RIGHT]);
                                tip.positionPopover({ left: medianX + offset, top: location_2[1], width: 0, height: 0 }, null, false);
                                _drawTimeCalloutLinesAndMarkers(svgContainer, location_2, targetLocation, goLeft, medianX);
                            }
                        }
                    }
                }
                _this._timeoutId = null;
            }, 200);
        }
        this._currentTipData = newTipData;
    };
    /**
     * [hideTooltips description]
     * @param {string} [id] hide the tips associated with the given id.
     *     If id is null then all tooltips are hidden.
     * @param {boolean} [animate=true] Fade out the tooltips or have them disappear immediately.
     */
    TooltipManager.prototype.hideTooltips = function (id, animate) {
        if (animate === undefined) {
            animate = true;
        }
        if (!this._currentTipData || this._currentTipData.length === 0) {
            return;
        }
        if (this._timeoutId !== null) {
            if (id) {
                // remove tip associated with the given id from list of tips that will be displayed.
                for (var i = this._currentTipData.length - 1; i >= 0; --i) {
                    if (this._currentTipData[i].containerId === id) {
                        this._currentTipData.splice(i, 1);
                    }
                }
                if (!this._currentTipData.length) {
                    clearTimeout(this._timeoutId);
                    this._timeoutId = null;
                }
            }
            else {
                // clear display of future tips
                this._currentTipData = null;
                clearTimeout(this._timeoutId);
                this._timeoutId = null;
            }
        }
        else {
            // if no timeout, then we assume _tooltipPool & _currentTipData is in sync.
            for (var i = this._tooltipPool.length - 1; i >= 0; --i) {
                if (id && this._currentTipData.length > i) {
                    // hide tips associated with the given ido
                    if (this._currentTipData[i].containerId === id) {
                        this._tooltipPool[i].setDisplay(Display.NONE, animate);
                        this._currentTipData.splice(i, 1);
                    }
                }
                else {
                    // else hide all tips
                    this._tooltipPool[i].setDisplay(Display.NONE, animate);
                }
            }
        }
        if (!id) {
            this._currentTipData = [];
        }
        var svgContainer = document.querySelector('.sasGraphTooltipCalloutLinesAndMarkers');
        if (svgContainer && svgContainer.firstChild) {
            var svgElement = svgContainer.firstChild;
            while (svgElement.firstChild) {
                svgElement.removeChild(svgElement.firstChild);
            }
        }
    };
    TooltipManager.instance = function () {
        if (!_instance) {
            _instance = new TooltipManager(_instanceCheck);
        }
        return _instance;
    };
    /**
     * Set the callback for determining ZIndex for tooltips. Used for non-openUI applications.
     *     This is useful when the zIndex may be dependent on other html elements such as popups.
     *     The callback
     *
     * @param {Function} callback the function that will be called before rendering the tooltip.
     *     Parameters:
     *         {int}  zIndex the most recently used zIndex used to render a tooltip.
     *     Returns:
     *         {int} zIndex to be used to render the current tooltip.
     */
    TooltipManager.setZIndexFuction = function (callback) {
        if (sas.ltjs.isOpenUI) {
            throw new Error('setZIndexFuction should not be used for openUI applications');
        }
        _getNextZIndex = callback;
    };
    /**
     * Set the current ZIndex for rendering tootips. Used for non-openUI applications.
     */
    TooltipManager.setZIndex = function (value) {
        if (sas.ltjs.isOpenUI) {
            throw new Error('setZIndex should not be used for openUI applications');
        }
        // NOTE(mikcol): was currentZIndex = callback
        currentZIndex = value;
    };
    return TooltipManager;
}());
export default TooltipManager;
